import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Save, Plus, ChevronUp, ChevronDown, Trash2, Monitor, Smartphone, Loader2 } from "lucide-react";
import { LandingPageView } from "../components/LandingSections";

const BACKEND_HOST = API.replace(/\/api$/, "");

const SECTION_LIBRARY = [
  { type: "hero", label: "Hero" },
  { type: "product_showcase", label: "Produkte" },
  { type: "features", label: "Features" },
  { type: "testimonials", label: "Testimonials" },
  { type: "lead_form", label: "Lead-Formular" },
  { type: "cta_banner", label: "CTA Banner" },
  { type: "text_block", label: "Text" },
  { type: "footer", label: "Footer" },
];

const newSection = (type) => {
  const id = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : "s-" + Math.random().toString(36).slice(2);
  const defaults = {
    hero: { headline: "Neuer Hero", subline: "Beschreibung …", cta_label: "Mehr erfahren" },
    product_showcase: { heading: "Unsere Produkte", product_ids: [] },
    features: { heading: "Vorteile", items: [{ title: "Vorteil 1", text: "Beschreibung" }, { title: "Vorteil 2", text: "Beschreibung" }] },
    testimonials: { heading: "Stimmen", items: [{ quote: "Beste Pflege, die ich kenne.", author: "Marie" }] },
    lead_form: { heading: "Bleib in Kontakt", intro: "Trag dich für Updates ein." },
    cta_banner: { heading: "Jetzt starten", cta_label: "Kaufen", cta_url: "" },
    text_block: { heading: "Abschnitt", text: "Fließtext …" },
    footer: { text: "© Deine Marke · Impressum · Datenschutz" },
  };
  return { id, type, props: defaults[type] || {} };
};

export default function SeitenEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [page, setPage] = useState(null);
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [viewport, setViewport] = useState("desktop");
  const timerRef = useRef(null);

  const load = async () => {
    const [p, pr] = await Promise.all([api.get(`/landing-pages/${id}`), api.get("/products")]);
    setPage(p.data);
    setProducts(pr.data);
  };
  useEffect(() => { load(); }, [id]);

  const save = async (next = page) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/landing-pages/${id}`, {
        title: next.title, slug: next.slug, product_refs: next.product_refs,
        sections: next.sections, theme: next.theme, seo: next.seo,
        cta: next.cta, lead_form: next.lead_form,
      });
      setPage(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  // Debounced autosave on page changes
  const scheduleSave = (next) => {
    setPage(next);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => save(next), 700);
  };

  const sections = page?.sections || [];
  const sel = sections[selectedIdx] || null;

  const updateSelected = (newProps) => {
    const next = { ...page, sections: sections.map((s, i) => i === selectedIdx ? { ...s, props: { ...s.props, ...newProps } } : s) };
    scheduleSave(next);
  };
  const moveSection = (dir) => {
    const i = selectedIdx; const j = i + dir;
    if (j < 0 || j >= sections.length) return;
    const next = [...sections];
    [next[i], next[j]] = [next[j], next[i]];
    scheduleSave({ ...page, sections: next });
    setSelectedIdx(j);
  };
  const removeSection = () => {
    const next = sections.filter((_, i) => i !== selectedIdx);
    scheduleSave({ ...page, sections: next });
    setSelectedIdx(Math.max(0, selectedIdx - 1));
  };
  const addSection = (type) => {
    const next = [...sections, newSection(type)];
    scheduleSave({ ...page, sections: next });
    setSelectedIdx(next.length - 1);
  };

  if (!page) {
    return <div className="p-10 text-slate-400" data-testid="seiten-editor-loading">Lade Seite…</div>;
  }

  const previewWidth = viewport === "mobile" ? "max-w-[420px]" : "max-w-full";

  return (
    <div className="p-4 lg:p-6 max-w-[1600px] mx-auto" data-testid="seiten-editor-page">
      <header className="flex items-center gap-3 mb-4">
        <Button variant="ghost" size="sm" onClick={() => nav("/seiten")} data-testid="editor-back" className="text-slate-300">
          <ArrowLeft className="h-4 w-4 mr-1" /> Zurück
        </Button>
        <div className="flex-1 min-w-0">
          <Input value={page.title} onChange={(e) => scheduleSave({ ...page, title: e.target.value })}
            data-testid="editor-title" className="bg-white/5 border-white/10 text-white max-w-md" />
          <div className="text-[11px] text-slate-500 mt-1">/p/{page.slug}</div>
        </div>
        <Badge className={`${page.status === "published" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : page.status === "unpublished" ? "bg-amber-500/15 text-amber-300" : "bg-slate-500/15 text-slate-300"} border font-normal`}>
          {page.status === "published" ? "Online" : page.status === "unpublished" ? "Offline" : "Entwurf"}
        </Badge>
        <div className="flex rounded-md bg-white/5 p-0.5">
          <button onClick={() => setViewport("desktop")} data-testid="viewport-desktop"
            className={`px-2 py-1 rounded ${viewport === "desktop" ? "bg-white/10 text-white" : "text-slate-400"}`}><Monitor className="h-4 w-4" /></button>
          <button onClick={() => setViewport("mobile")} data-testid="viewport-mobile"
            className={`px-2 py-1 rounded ${viewport === "mobile" ? "bg-white/10 text-white" : "text-slate-400"}`}><Smartphone className="h-4 w-4" /></button>
        </div>
        <Button onClick={() => save()} disabled={saving} data-testid="editor-save" className="bg-emerald-500 hover:bg-emerald-600">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Speichern
        </Button>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr_320px] gap-4">
        {/* Section list */}
        <Card className="bg-[#111827] border-white/5 h-fit">
          <CardContent className="p-3">
            <div className="text-xs text-slate-400 mb-2 uppercase tracking-wider">Sections</div>
            <div className="space-y-1" data-testid="section-list">
              {sections.map((s, i) => (
                <button key={s.id} onClick={() => setSelectedIdx(i)}
                  data-testid={`section-item-${i}`}
                  className={`w-full text-left px-3 py-2 rounded text-sm transition ${
                    i === selectedIdx ? "bg-white/10 text-white" : "text-slate-400 hover:text-white hover:bg-white/5"
                  }`}>
                  <span className="text-[10px] uppercase tracking-wider opacity-60 mr-1">{i + 1}</span>
                  {SECTION_LIBRARY.find((x) => x.type === s.type)?.label || s.type}
                </button>
              ))}
            </div>
            <div className="mt-4 border-t border-white/5 pt-3">
              <div className="text-[11px] text-slate-500 mb-2">+ Section hinzufügen</div>
              <div className="flex flex-wrap gap-1">
                {SECTION_LIBRARY.map((l) => (
                  <button key={l.type} onClick={() => addSection(l.type)} data-testid={`add-section-${l.type}`}
                    className="text-[11px] px-2 py-1 rounded border border-white/10 text-slate-400 hover:text-white hover:bg-white/5">
                    {l.label}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Preview */}
        <div className="rounded-xl overflow-hidden bg-white/5">
          <div className="bg-[#0b0f17] text-xs text-slate-500 px-3 py-2 flex items-center justify-between">
            <span>Vorschau · {viewport}</span>
            {saving && <span className="flex items-center gap-1 text-slate-400"><Loader2 className="h-3 w-3 animate-spin" /> wird gespeichert…</span>}
          </div>
          <div className={`mx-auto overflow-auto ${previewWidth} max-h-[70vh]`} data-testid="editor-preview">
            <LandingPageView page={page} products={products} theme={page.theme} backendHost={BACKEND_HOST}
              leadForm={page.lead_form} isPreview={true} />
          </div>
        </div>

        {/* Right panel: edit selected section + theme/seo/leadform */}
        <div className="space-y-3">
          {sel && (
            <Card className="bg-[#111827] border-white/5" data-testid="section-editor">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-slate-400 uppercase tracking-wider">{sel.type}</div>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" onClick={() => moveSection(-1)} data-testid="section-move-up" className="h-7 w-7 text-slate-400"><ChevronUp className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => moveSection(1)} data-testid="section-move-down" className="h-7 w-7 text-slate-400"><ChevronDown className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={removeSection} data-testid="section-remove" className="h-7 w-7 text-red-400"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
                <PropsEditor type={sel.type} props={sel.props} onChange={updateSelected} products={products} />
              </CardContent>
            </Card>
          )}

          <Card className="bg-[#111827] border-white/5" data-testid="theme-editor">
            <CardContent className="p-4 space-y-3">
              <div className="text-xs text-slate-400 uppercase tracking-wider">Theme</div>
              <div>
                <Label className="text-slate-300 text-xs">Primärfarbe</Label>
                <Input type="color" value={page.theme.primary_color} onChange={(e) => scheduleSave({ ...page, theme: { ...page.theme, primary_color: e.target.value } })}
                  data-testid="theme-primary" className="h-9 bg-white/5 border-white/10" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Hintergrund</Label>
                <Select value={page.theme.background} onValueChange={(v) => scheduleSave({ ...page, theme: { ...page.theme, background: v } })}>
                  <SelectTrigger data-testid="theme-background"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="light">Hell</SelectItem>
                    <SelectItem value="dark">Dunkel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Schrift</Label>
                <Select value={page.theme.font} onValueChange={(v) => scheduleSave({ ...page, theme: { ...page.theme, font: v } })}>
                  <SelectTrigger data-testid="theme-font"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="jakarta">Plus Jakarta Sans</SelectItem>
                    <SelectItem value="inter">Inter</SelectItem>
                    <SelectItem value="serif">Serif</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-slate-300 text-xs">Logo zeigen</Label>
                <Switch checked={page.theme.show_logo} onCheckedChange={(v) => scheduleSave({ ...page, theme: { ...page.theme, show_logo: v } })} data-testid="theme-logo-toggle" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#111827] border-white/5" data-testid="seo-editor">
            <CardContent className="p-4 space-y-3">
              <div className="text-xs text-slate-400 uppercase tracking-wider">SEO</div>
              <div>
                <Label className="text-slate-300 text-xs">Titel</Label>
                <Input value={page.seo.title} onChange={(e) => scheduleSave({ ...page, seo: { ...page.seo, title: e.target.value } })}
                  data-testid="seo-title" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Beschreibung</Label>
                <Textarea rows={2} value={page.seo.description} onChange={(e) => scheduleSave({ ...page, seo: { ...page.seo, description: e.target.value } })}
                  data-testid="seo-description" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#111827] border-white/5" data-testid="leadform-editor">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-xs text-slate-400 uppercase tracking-wider">Lead-Formular</div>
                <Switch checked={page.lead_form.enabled} onCheckedChange={(v) => scheduleSave({ ...page, lead_form: { ...page.lead_form, enabled: v } })} data-testid="leadform-enabled" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Consent-Text</Label>
                <Textarea rows={2} value={page.lead_form.consent_text} onChange={(e) => scheduleSave({ ...page, lead_form: { ...page.lead_form, consent_text: e.target.value } })}
                  data-testid="leadform-consent" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#111827] border-white/5">
            <CardContent className="p-4 space-y-2">
              <div className="text-xs text-slate-400 uppercase tracking-wider">Produkt-Zuordnung</div>
              <div className="flex flex-wrap gap-1" data-testid="page-products">
                {products.map((prod) => {
                  const on = (page.product_refs || []).includes(prod.id);
                  return (
                    <button key={prod.id} onClick={() => scheduleSave({
                      ...page,
                      product_refs: on ? page.product_refs.filter((x) => x !== prod.id) : [...(page.product_refs || []), prod.id],
                    })}
                      data-testid={`page-product-${prod.id}`}
                      className={`text-[11px] px-2 py-1 rounded border ${on ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-300" : "bg-transparent border-white/10 text-slate-400 hover:text-white"}`}>
                      {prod.name}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function PropsEditor({ type, props, onChange, products }) {
  const Field = ({ k, label, type: inputType = "text", multiline }) => (
    <div>
      <Label className="text-slate-300 text-xs">{label}</Label>
      {multiline
        ? <Textarea value={props[k] || ""} onChange={(e) => onChange({ [k]: e.target.value })} data-testid={`field-${k}`} rows={2} className="bg-white/5 border-white/10 text-slate-100" />
        : <Input type={inputType} value={props[k] || ""} onChange={(e) => onChange({ [k]: e.target.value })} data-testid={`field-${k}`} className="bg-white/5 border-white/10 text-slate-100" />}
    </div>
  );

  if (type === "hero") return (<>
    <Field k="headline" label="Headline" />
    <Field k="subline" label="Subline" multiline />
    <Field k="image_url" label="Bild-URL" />
    <Field k="cta_label" label="CTA-Label" />
  </>);
  if (type === "product_showcase") return (<>
    <Field k="heading" label="Überschrift" />
    <Label className="text-slate-300 text-xs">Produkte (leer = alle verknüpften)</Label>
    <div className="flex flex-wrap gap-1">
      {products.map((prod) => {
        const on = (props.product_ids || []).includes(prod.id);
        return (
          <button type="button" key={prod.id} onClick={() => onChange({
            product_ids: on ? (props.product_ids || []).filter((x) => x !== prod.id) : [...(props.product_ids || []), prod.id],
          })}
            className={`text-[11px] px-2 py-1 rounded border ${on ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-300" : "bg-transparent border-white/10 text-slate-400 hover:text-white"}`}>
            {prod.name}
          </button>
        );
      })}
    </div>
  </>);
  if (type === "features") return (<>
    <Field k="heading" label="Überschrift" />
    <div className="text-[11px] text-slate-500">Items: {(props.items || []).length}</div>
    {(props.items || []).map((it, i) => (
      <div key={i} className="rounded border border-white/10 p-2 space-y-1">
        <Input value={it.title || ""} placeholder="Titel"
          onChange={(e) => onChange({ items: props.items.map((x, j) => j === i ? { ...x, title: e.target.value } : x) })}
          data-testid={`features-title-${i}`} className="bg-white/5 border-white/10 text-slate-100 h-8" />
        <Textarea value={it.text || ""} placeholder="Text" rows={2}
          onChange={(e) => onChange({ items: props.items.map((x, j) => j === i ? { ...x, text: e.target.value } : x) })}
          data-testid={`features-text-${i}`} className="bg-white/5 border-white/10 text-slate-100" />
      </div>
    ))}
    <Button type="button" size="sm" variant="outline" onClick={() => onChange({ items: [...(props.items || []), { title: "", text: "" }] })}
      data-testid="features-add" className="border-white/10 bg-transparent text-slate-300"><Plus className="h-3 w-3 mr-1" /> Feature</Button>
  </>);
  if (type === "testimonials") return (<>
    <Field k="heading" label="Überschrift" />
    {(props.items || []).map((it, i) => (
      <div key={i} className="rounded border border-white/10 p-2 space-y-1">
        <Textarea value={it.quote || ""} placeholder="Zitat" rows={2}
          onChange={(e) => onChange({ items: props.items.map((x, j) => j === i ? { ...x, quote: e.target.value } : x) })} className="bg-white/5 border-white/10 text-slate-100" />
        <Input value={it.author || ""} placeholder="Autor"
          onChange={(e) => onChange({ items: props.items.map((x, j) => j === i ? { ...x, author: e.target.value } : x) })} className="bg-white/5 border-white/10 text-slate-100 h-8" />
      </div>
    ))}
    <Button type="button" size="sm" variant="outline" onClick={() => onChange({ items: [...(props.items || []), { quote: "", author: "" }] })}
      className="border-white/10 bg-transparent text-slate-300"><Plus className="h-3 w-3 mr-1" /> Stimme</Button>
  </>);
  if (type === "lead_form") return (<><Field k="heading" label="Überschrift" /><Field k="intro" label="Intro-Text" multiline /></>);
  if (type === "cta_banner") return (<><Field k="heading" label="Überschrift" /><Field k="cta_label" label="CTA-Label" /><Field k="cta_url" label="CTA-URL" /></>);
  if (type === "text_block") return (<><Field k="heading" label="Überschrift" /><Field k="text" label="Text" multiline /></>);
  if (type === "footer") return (<Field k="text" label="Footer-Text" />);
  return <div className="text-xs text-slate-500">Keine Props</div>;
}
